import React, {useCallback, useEffect, useState} from "react";
import Api from "../../../../core/services/api/api";
import useIsMounted from "../../../hooks/use-is-mounted";
import {routes} from "../../../../core/models/static/routes/app-routes";
import * as Yup from "yup";
import ValidateMessages from "../../../../core/models/static/texts/vallidate-messages";
import {makeRequired, makeValidate} from "mui-rff";
import Dialog from "../../base/dialog";
import {DialogActions, DialogContent, DialogTitle, Divider} from "@mui/material";
import {Col} from "reactstrap";
import Form from "../../base/form";
import {OutlinedInputForm} from "../../base/input/outlined_input";
import Button from "../../base/button/button";
import {numComparator} from "../../../../core/services/utils/utils";
import TryAgain from "../../app-specific/try-again";
import {ReactSortable} from "react-sortablejs";
import NotificationCategoryCard from "../../app-specific/notification-cards/category-notification-card";
import {useHistory} from "react-router-dom";
import useAccessLevels from "../../../hooks/use-access-levels";


const schema = Yup.object().shape({
    newTitle: Yup.string().nullable().required(ValidateMessages.required)
});

const subscription = {submitting: true, values: true, initialValues: true, submitErrors: true};


const NotificationCategoryUpsertModal = ({open, setOpen}) => {
    const history = useHistory();
    const [categories, _setCategories] = useState([])
    const [loading, setLoading] = useState(false)
    const [creating, setCreating] = useState(false)
    const isMounted = useIsMounted()
    const {packages} = useAccessLevels();
    const canManageList = packages.notifications.manageList;
    const validate = makeValidate(schema);
    const required = makeRequired(schema);

    /**
     * Sets the categories state of this component by sorting them based on their orderIndex after the new state is
     * received.
     *
     * This method acts as a wrapper that injects the sorting behaviour at the end of the setting this state.
     * @type {function(*=, boolean): void}
     */
    const setCategories = useCallback((stateOrCallback, sort = true) => {
        _setCategories((prevState) => {
            let newState;
            if (typeof stateOrCallback === 'function') {
                newState = stateOrCallback(prevState)
            } else {
                newState = stateOrCallback
            }
            if (sort) {
                return newState?.sort((a, b) => numComparator(a.orderIndex, b.orderIndex)) ?? []
            }
            return newState
        })
    }, [])

    /**
     * Handles the api errors when dealing with Biz-live system.
     *
     * in both cases, navigates the user to access denied view
     * @type {function(*=): void}
     */
    const unAuthorizedOrAccessDeniedCallback = useCallback((response) => {
        if (!isMounted()) return;
        switch (response?.errorCode) {
            case 401:
            case 403:
            case -401:
            case -403:
                history.push(history.location.pathname + routes.error.accessDeniedForSection)
                break;
            default:
                break;
        }
    }, [isMounted, history])

    /**
     * Fetches the notification categories of the system form the server and populates the state list.
     * @type {function(): Promise<void>}
     */
    const getCategories = useCallback(async () => {
        setLoading(true);
        const response = await Api.getAllNotificationCategories(unAuthorizedOrAccessDeniedCallback);
        if (!isMounted())
            return
        if (response?.resultFlag) {
            setCategories(response?.data?.map(e => ({...e, editing: false, inEditMode: false})) ?? [])
        }
        setLoading(false);
    }, [isMounted, setCategories, unAuthorizedOrAccessDeniedCallback])

    /**
     * Listens for the changes in the open state of this dialog and with each change:
     * if the dialog is opened, fetches the notification categories from the server.
     */
    useEffect(() => {
        if (open) {
            void getCategories();
        }
    }, [getCategories, open])

    /**
     * Create a new category with the given values and adds it to the list of categories.
     * @param {any} values the form values
     * @param {formApi} form the form
     * @return {Promise<boolean>}
     */
    const createCategory = async (values, form) => {
        if (!canManageList) return false;
        setCreating(true)
        const forApi = {
            title: values.newTitle,
        }
        const response = await Api.createNotificationCategory(forApi)
        if (!isMounted()) return false
        if (response?.resultFlag) {
            setCategories(prevState => [
                ...prevState,
                {...response.data, editing: false, inEditMode: false}
            ])
            form.change('newTitle', '')
            form.resetFieldState('newTitle');
        }
        setCreating(false)
        return false
    }

    /**
     * Updates a category with the given values.
     * @param {any} category the category to be updated.
     * @return {Promise<boolean>}
     */
    const updateCategory = async (category) => {
        if (!canManageList) return false;
        setCategories(prevState => prevState.map(e => e.id === category.id ? {
            ...e,
            editing: true
        } : e))
        const forApi = {
            ...category,
        }
        const response = await Api.updateNotificationCategory(forApi, unAuthorizedOrAccessDeniedCallback)
        if (!isMounted()) return false
        setCategories(prevState => {
            const newList = prevState.map(e => e.id === category.id
                ? {
                    ...e,
                    editing: false,
                    ...(response?.resultFlag ? forApi : {})
                }
                : e)
            return newList.sort((a, b) => numComparator(a.orderIndex, b.orderIndex))
        })
        return response?.resultFlag ?? false
    }

    /**
     * Removes a category from the server and then removes it from the local list.
     * @param {any} category the category to be removed
     */
    const removeCategory = (category) => {
        if (!canManageList) return;
        setCategories(prevState => prevState.map(e => e.id === category.id ? {
            ...e,
            editing: true
        } : e))
        Api.removeNotificationCategory(category.id, unAuthorizedOrAccessDeniedCallback).then((response) => {
            if (!isMounted()) return
            if (response?.resultFlag) {
                setCategories(prevState => prevState.filter(e => e.id !== category.id))
            } else {
                setCategories(prevState => prevState.map(e => e.id === category.id ? {
                    ...e,
                    editing: false
                } : e))
            }
        })
    }

    /**
     * Updates the notification categories index order from the server.
     *
     * Note: This api is called after the dialog is closed, and thus its response is almost ignored.
     * @return {Promise<any[]>} the categories
     */
    const updateCategoriesOrder = async () => {
        if (!canManageList) return categories;
        const forApi = {
            ids: categories?.map(e => e.id) ?? [],
        }
        if (forApi.ids.length < 2) {
            return categories;
        }
        const response = await Api.updateNotificationCategoriesOrder(forApi, unAuthorizedOrAccessDeniedCallback)
        if (!isMounted()) return []
        if (response?.resultFlag) {
            return categories.map((e, i) => ({...e, orderIndex: i + 1}))
        }
        return categories
    }

    /**
     * Closes the dialog by passing the new categories to the calling component.
     * @param {boolean} open
     */
    const onClose = (open) => {
        updateCategoriesOrder().then((categories) => {
            setOpen(open, categories)
        })
    }

    /**
     * Contains The new list after being rearranged and sorted.
     * @param {any[]} newCategories
     */
    const rearrangeCategories = (newCategories) => {
        setCategories(newCategories, false)
    }

    /**
     * Toggles the edit mode of the category cards.
     * @param {any} category
     * @param {boolean} inEditMode
     */
    const toggleCategoryEditMode = (category, inEditMode = false) => {
        setCategories(prevState => prevState.map(e => e.id === category.id ? {...e, inEditMode} : e))
    }

    return (
        <>
            <Dialog
                open={open}
                setOpen={onClose}
                maxWidth={'md'}
                loading={loading || creating}
                className={'upsert-notification-category-dialog bound-dialog'}
                scroll={'paper'}
                aria-labelledby="scroll-dialog-title"
            >
                <DialogTitle id={'scroll-dialog-title'}>
                    <p className={`text-lg text-color-1 my-3 font-weight-bold text-center`}>
                        Edit Groups
                    </p>
                    <Divider className='divider'/>
                </DialogTitle>
                <DialogContent className={''}>
                    <Col xs={12}>
                        <p className={'text-md font-weight-semi-bold mt-2 mb-4'}>
                            Create a new Category
                        </p>
                    </Col>
                    <Form
                        className='d-flex flex-wrap w-100 align-items-center'
                        onSubmit={createCategory}
                        subscription={subscription}
                        validate={validate}
                        initialValues={{}}
                        render={({values, form}) => {
                            return (
                                <>
                                    <Col className={'pl-3'}>
                                        <OutlinedInputForm
                                            name={'newTitle'}
                                            placeholder={'title'}
                                            label={"New Title"}
                                            required={required.newTitle}
                                            value={values.newTitle ?? ''}
                                            onChange={(e) => form.change(e.target.name, e.target.value)}
                                        />
                                    </Col>
                                    <Col xs={'auto'}>
                                        <Button
                                            className='py-2-5 px-5'
                                            type={'submit'}
                                            variant={'contained'}
                                            color={'primary'}
                                            disabled={creating}
                                        >
                                            {
                                                creating
                                                    ? "Adding..."
                                                    : "Add"
                                            }
                                        </Button>
                                    </Col>
                                </>
                            )
                        }}
                    />
                    <Col xs={12} className={'mt-3 d-flex flex-wrap mb-5'}>
                        {
                            loading
                                ? Array(4).fill(null).map((e, i) => (
                                    <div key={i} className={'loading normal w-100 mb-3'}>
                                        <div/>
                                    </div>
                                ))
                                : categories?.length < 1
                                    ? <TryAgain
                                        text={"There is currently no categories submitted. you can add one  using" +
                                            " the form above."}
                                    />
                                    : <ReactSortable
                                        className={'categories-container'}
                                        list={categories ?? []}
                                        sort={true}
                                        setList={rearrangeCategories}
                                        handle={'.handle'}
                                        group={'categories'}
                                        animation={210}
                                        forceFallback={false}
                                        swapThreshold={0.65}
                                        ghostClass={"dragged-category"}
                                    >
                                        {
                                            categories?.map(category => (
                                                <NotificationCategoryCard
                                                    key={category.id}
                                                    category={category}
                                                    update={updateCategory}
                                                    remove={removeCategory}
                                                    setEditing={(e) => toggleCategoryEditMode(category, e)}
                                                    sortable={!categories?.some(e => !!e.inEditMode)}
                                                />
                                            ))
                                        }
                                    </ReactSortable>
                        }
                    </Col>
                </DialogContent>
                <DialogActions/>
            </Dialog>
        </>
    )
}

export default NotificationCategoryUpsertModal;
